import "moment-timezone";
import moment from "moment";
import Field from "./field";
import DocumentValue from "./document-value";
import { getDateonlyIso, normalize } from "../utils";
import IPeriodValue from "../interface/period-value";
import IConfigFormulaRenewal from "../interface/field-cfg-formula-renewal";
import IConfigFormula from "../interface/field-cfg-formula";
import IConfigDatetime from "../interface/field-cfg-datetime";
import IField from "../interface/field";
import IEntityFilterValue from "../interface/entity-filter-value";
import IEntityFilterPrepared from "../interface/entity-filter-prepared";
import IEntityFilterDatePeriod from "../interface/entity-filter-date-period";
import IEntityFilter from "../interface/entity-filter";
import IDocumentValue from "../interface/document-value";
import IDocument from "../interface/document";
import IDateComplexValue from "../interface/date-complex-value";
import IDateComplexPeriodDirection from "../interface/date-complex-period-direction";
import Global from "../global";
import LogicalValue from "../enum/logical-value";
import FormulaType from "../enum/formula-type";
import FieldType from "../enum/field-type";
import ErrorCode from "../enum/error-code";
import EntityFilterRule from "../enum/entity-filter-rule";
import DatePeriodDirection from "../enum/date-period-direction";
import DatePeriod from "../enum/date-period";
import DateComplexType from "../enum/date-complex-type";
import DateComplexConst from "../enum/date-complex-const";
import DateFilterValue from "../class/date-filter-value";

class CollectionFiltersInstance {

    private readonly DateISO: string = 'YYYY-MM-DD';

    /* Public Methods */

    public filter(doc: IDocument, filter: IEntityFilterPrepared): boolean {
        let value: IDocumentValue = this.getValue(DocumentValue.getByPathSync(doc, filter.pathId));
        switch (filter.field.uiType) {
            case FieldType.text:
            case FieldType.email:
            case FieldType.textarea:
                return this.text(filter, value);
            case FieldType.number:
                return this.number(filter, value);
            case FieldType.datetime:
                return this.datetime(filter, value);
            case FieldType.linked:
                const path: number[] = [...filter.pathId.slice(0, -1), filter.field.linkedRelation[0]];
                value = this.getValue(DocumentValue.getByPathSync(doc, path));
                return this.linked(filter, value);
            case FieldType.check:
                return this.check(filter, value);
            case FieldType.formula:
                value = this.getValue(doc[`${Global.FORMULA_PROP_PREFIX}${filter.field.id}`]);
                return this.formula(filter, value, filter.filterType);
            default:
                return false;
        }
    }

    public filterText(values: string[], patterns: string[]): boolean {
        for (let i = 0; i < patterns.length; i++) {
            let r: boolean = false;
            for (let j = 0; j < values.length; j++) {
                if (values[j].includes(patterns[i])) {
                    r = true;
                    break;
                }
            }
            if (!r) {
                return false;
            }
        }
        return true;
    }

    public prepare(filters: IEntityFilter[]): IEntityFilterPrepared[] {
        const result: IEntityFilterPrepared[] = [];
        this.getValidFilters(filters).forEach((filter: IEntityFilter) => {
            const field: IField = Field.get(filter.pathId.slice(-1)[0]) as IField;
            const prepared: IEntityFilterPrepared = {
                field: field,
                pathId: filter.pathId,
                filterType: filter.fieldType,
                rule: filter.rule,
                valueMin: null,
                valueMax: null,
                period: null
            };
            switch (filter.fieldType) {
                case FieldType.text:
                case FieldType.textarea:
                case FieldType.email:
                    prepared.valueMin = filter.valueMin !== null ? normalize(((filter.valueMin as string) || '').toLowerCase()) : null;
                    break;
                case FieldType.number:
                    prepared.valueMin = filter.valueMin !== null ? (filter.valueMin as number) : null;
                    prepared.valueMax = filter.valueMax !== null ? (filter.valueMax as number) : null;
                    break;
                case FieldType.datetime:
                    if (filter.valueMin !== null) {
                        prepared.valueMin = this.getDateFilterValue(filter.valueMin as IEntityFilterValue, field);
                    }
                    if (filter.valueMax !== null) {
                        prepared.valueMax = this.getDateFilterValue(filter.valueMax as IEntityFilterValue, field);
                    }
                    break;
                case FieldType.linked:
                    prepared.valueMin = filter.valueMin !== null ? (filter.valueMin as number[]) : null;
                    break;
                case FieldType.check:
                    prepared.valueMin = filter.valueMin as LogicalValue;
                    break;
                case FieldType.formula:
                    const config: IConfigFormula = field.uiConfig as IConfigFormula;
                    const renewal: IConfigFormulaRenewal = config.formula as IConfigFormulaRenewal;
                    if (config.type === FormulaType.number) {
                        prepared.filterType = FieldType.number;
                        prepared.valueMin = filter.valueMin !== null ? (filter.valueMin as number) : null;
                        prepared.valueMax = filter.valueMax !== null ? (filter.valueMax as number) : null;
                    } else if (config.type === FormulaType.renewal && !renewal.showPeriod) {
                        prepared.filterType = FieldType.datetime;
                        if (filter.valueMin !== null) {
                            prepared.valueMin = this.getDateFilterValue(filter.valueMin as IEntityFilterValue, field);
                        }
                        if (filter.valueMax !== null) {
                            prepared.valueMax = this.getDateFilterValue(filter.valueMax as IEntityFilterValue, field);
                        }
                    } else {
                        prepared.filterType = FieldType.renewal;
                        if (filter.valueMin !== null) {
                            prepared.period = (filter.valueMin as IEntityFilterDatePeriod).period;
                            prepared.valueMin = (filter.valueMin as IEntityFilterDatePeriod).offset;
                        }
                        prepared.valueMax = filter.valueMax !== null ? (filter.valueMax as IEntityFilterDatePeriod).offset : null;
                    }
                    break;
            }
            result.push(prepared);
        });
        return result;
    }

    public process(filters: IEntityFilter[]): IEntityFilter[] {
        const result: IEntityFilter[] = this.getValidFilters(filters);
        result.forEach((filter: IEntityFilter) => {
            if (filter.fieldType === FieldType.datetime) {
                filter.valueMin = this.processComplexDateValue(filter.valueMin);
                filter.valueMax = this.processComplexDateValue(filter.valueMax);
            } else if (filter.fieldType === FieldType.formula) {
                const field: IField | null = Field.get(filter.pathId.slice(-1)[0]);
                if (field) {
                    const config: IConfigFormula = field.uiConfig as IConfigFormula;
                    if (config.type === FormulaType.renewal) {
                        if (!(config.formula as IConfigFormulaRenewal).showPeriod) {
                            filter.valueMin = this.processComplexDateValue(filter.valueMin);
                            filter.valueMax = this.processComplexDateValue(filter.valueMax);
                        }
                    }
                }
            }
        });
        return result;
    }

    public getValidFilters(filters: IEntityFilter[]): IEntityFilter[] {
        return filters.filter((filter: IEntityFilter) => {
            for (let i = 0; i < filter.pathId.length; i++) {
                const field: IField | null = Field.get(filter.pathId[i]);
                if (!field) {
                    return false;
                }
                if (i === filter.pathId.length - 1) {
                    return field.uiType === filter.fieldType;
                }
            }
        });
    }

    /* Private Methods - Filters by field type */

    private text(filter: IEntityFilterPrepared, value: IDocumentValue): boolean {
        switch (filter.rule) {
            case EntityFilterRule.TextEmpty:
            case EntityFilterRule.TextareaEmpty:
            case EntityFilterRule.EmailEmpty:
                return this.isEmptyValue(value);
            case EntityFilterRule.TextNotEmpty:
            case EntityFilterRule.TextareaNotEmpty:
            case EntityFilterRule.EmailNotEmpty:
                return !this.isEmptyValue(value);
            case EntityFilterRule.TextEquals:
            case EntityFilterRule.TextareaEquals:
            case EntityFilterRule.EmailEquals:
                return DocumentValue.getString(value) === filter.valueMin as string;
            case EntityFilterRule.TextContains:
            case EntityFilterRule.TextareaContains:
            case EntityFilterRule.EmailContains:
                return DocumentValue.getString(value).includes(filter.valueMin as string);
        }
        return false;
    }

    private number(filter: IEntityFilterPrepared, value: IDocumentValue): boolean {
        if (filter.rule === EntityFilterRule.NumberEmpty) {
            return this.isEmptyValue(value);
        } else {
            const val: number | null = DocumentValue.getNumber(value);
            if (val !== null) {
                switch (filter.rule) {
                    case EntityFilterRule.FormulaEquals:
                    case EntityFilterRule.NumberEquals:
                        return val === (filter.valueMin as number);
                    case EntityFilterRule.FormulaBetween:
                    case EntityFilterRule.NumberBetween:
                        return val > (filter.valueMin as number) && val < (filter.valueMax as number);
                    case EntityFilterRule.FormulaMore:
                    case EntityFilterRule.NumberMore:
                        return val > (filter.valueMin as number);
                    case EntityFilterRule.FormulaLess:
                    case EntityFilterRule.NumberLess:
                        return val < (filter.valueMin as number);
                }
            }
        }
        return false;
    }

    private datetime(filter: IEntityFilterPrepared, value: IDocumentValue): boolean {
        if (filter.rule === EntityFilterRule.DatetimeEmpty) {
            return this.isEmptyValue(value);
        } else {
            const val: number | null = DocumentValue.getDatetime(value, filter.field);
            if (val !== null) {
                const dateMin: DateFilterValue = filter.valueMin as DateFilterValue;
                switch (filter.rule) {
                    case EntityFilterRule.FormulaEquals:
                    case EntityFilterRule.DatetimeEquals:
                        return val >= dateMin.start && val <= dateMin.end;
                    case EntityFilterRule.FormulaBetween:
                    case EntityFilterRule.DatetimeBetween:
                        const dateMax: DateFilterValue = filter.valueMax as DateFilterValue;
                        return val >= dateMin.start && val <= dateMax.end;
                    case EntityFilterRule.FormulaMore:
                    case EntityFilterRule.DatetimeAfter:
                        return val >= dateMin.end;
                    case EntityFilterRule.FormulaLess:
                    case EntityFilterRule.DatetimeBefore:
                        return val <= dateMin.start;
                }
            }
        }
        return false;
    }

    private linked(filter: IEntityFilterPrepared, value: IDocumentValue): boolean {
        switch (filter.rule) {
            case EntityFilterRule.LinkedHasValue:
                return !this.isEmptyValue(value)
            case EntityFilterRule.LinkedNoValue:
                return this.isEmptyValue(value)
            case EntityFilterRule.LinkedEquals:
                const val: number[] = DocumentValue.getLinked(value);
                return (filter.valueMin as number[]).includes(val[0]);
        }
        return false;
    }

    private check(filter: IEntityFilterPrepared, value: IDocumentValue): boolean {
        if (filter.rule === EntityFilterRule.LogicalEquals) {
            if (filter.valueMin === LogicalValue.True || filter.valueMin === LogicalValue.False) {
                return value === filter.valueMin;
            } else {
                return value !== LogicalValue.True && value !== LogicalValue.False;
            }
        }
        return false;
    }

    private formula(filter: IEntityFilterPrepared, value: IDocumentValue, type: FieldType): boolean {
        if (filter.rule === EntityFilterRule.FormulaNA) {
            return value === null;
        } else {
            switch(type) {
                case FieldType.number:
                    return this.number(filter, value);
                case FieldType.datetime:
                    return this.datetime(filter, value);
                case FieldType.renewal:
                    let val: number | null = null;
                    const periodValue: IPeriodValue | null = DocumentValue.getPeriod(value);
                    if (periodValue && periodValue.full) {
                        val = periodValue.full[filter.period as DatePeriod] * (periodValue.overdue ? -1 : 1);
                    }
                    return this.number(filter, val);
            }
        }
        return false;
    }

    /* Private Methods */

    private isEmptyValue(value: IDocumentValue): boolean {
        if (value instanceof Array) {
            return !value.length;
        } else {
            return value === '' || value === null;
        }
    }

    private getDateFilterValue(value: IEntityFilterValue, field: IField): DateFilterValue {
        const fieldCfg: IConfigDatetime = Field.getDatetimeConfig(field);
        var val: IDateComplexValue = value as IDateComplexValue;
        switch (val.type) {
            case DateComplexType.ExactDate:
                return new DateFilterValue(moment.utc(val.value as string).format(this.DateISO), fieldCfg);
            case DateComplexType.ConstantDate:
                switch (val.value) {
                    case DateComplexConst.MonthStart:
                        return new DateFilterValue(this.getMonthStart(fieldCfg), fieldCfg, true, false);
                    case DateComplexConst.MonthEnd:
                        return new DateFilterValue(this.getMonthEnd(fieldCfg), fieldCfg, false, true);
                }
                return new DateFilterValue(this.getToday(fieldCfg), fieldCfg);
            case DateComplexType.Period:
                const config: IDateComplexPeriodDirection = val.value as IDateComplexPeriodDirection;
                let offset: number = config.offset;
                if (config.direction === DatePeriodDirection.BeforeToday || config.direction === DatePeriodDirection.BeforeThisMonth) {
                    offset = offset * -1;
                }
                const basis: moment.Moment = this.getPeriodBasis(config.direction, fieldCfg);
                switch (config.period) {
                    case DatePeriod.Year:
                        basis.year(basis.year() + offset);
                        break;
                    case DatePeriod.Month:
                        basis.month(basis.month() + offset);
                        break;
                    case DatePeriod.Day:
                        basis.date(basis.date() + offset);
                        break;
                }
                return new DateFilterValue(basis.format(this.DateISO), fieldCfg);
            default:
                throw ErrorCode.BadArguments;
        }
    }

    private getPeriodBasis(direction: DatePeriodDirection, config: IConfigDatetime): moment.Moment {
        let iso: string = this.getToday(config);
        if (direction === DatePeriodDirection.AfterThisMonth) {
            iso = this.getMonthEnd(config);
        } else if (direction === DatePeriodDirection.BeforeThisMonth) {
            iso = this.getMonthStart(config);
        }
        return moment.utc(iso);
    }

    private getToday(config: IConfigDatetime): string {
        if (config.timezone) {
            return moment().tz(config.timezone).format(this.DateISO);
        } else {
            return moment().format(this.DateISO);
        }
    }

    private getMonthStart(config: IConfigDatetime): string {
        return moment.utc(this.getToday(config)).startOf('month').format(this.DateISO);
    }

    private getMonthEnd(config: IConfigDatetime): string {
        return moment.utc(this.getToday(config)).endOf('month').format(this.DateISO);
    }

    private processComplexDateValue(val: IEntityFilterValue | null): IDateComplexValue | null {
        if (val !== null) {
            const value: IDateComplexValue = val as IDateComplexValue;
            if (value.type === DateComplexType.ExactDate) {
                value.value = getDateonlyIso(value.value as string);
            }
            return value;
        }
        return null;
    }

    private getValue(value: IDocumentValue | undefined) {
        if (value === undefined) {
            return null;
        }
        return value;
    }

}

const CollectionFilters = new CollectionFiltersInstance();

export default CollectionFilters;